/**
 * Fee math for the Registration Setup → Payments "Processing Fees" block.
 *
 * Pure and React-agnostic so it can be unit tested directly. Mirrors the API's
 * invariant:
 *
 *   invoiceSubtotal + feeLines + tax === registrantPays
 *
 * A fee whose visibility flag is off is NOT dropped — it folds into
 * invoiceSubtotal so the document still reconciles.
 */

export type ProcessingFeeBearer = 'league' | 'registrant'

export type FeeLine = {
  key: 'league_processing_fee' | 'payment_processing_fee'
  label: string
  amount: number
}

export type FeePreviewInput = {
  /** Registration cost the fees are calculated against. */
  baseCost: number
  /** League fee percentage of baseCost. Null/0 contributes nothing. */
  leaguePercent: number | null
  /** League fee flat amount, added to the percentage. Null/0 contributes nothing. */
  leagueFlat: number | null
  /** Whether the league fee renders as its own line. */
  leagueVisible: boolean
  /** Who absorbs the payment processor's cost. */
  bearer: ProcessingFeeBearer
  /** Whether the processing fee renders as its own line. Inert while bearer is 'league'. */
  paymentVisible: boolean
  /** Combined rate of the competition's selected taxes, as a percentage. */
  taxRatePercent: number
  /** Processor's flat per-transaction fee, in dollars. */
  providerFlat: number
  /** Processor's percentage per-transaction fee. */
  providerPercent: number
}

export type FeePreviewResult = {
  /** Render this, never the raw baseCost — hidden fees are folded in here. */
  invoiceSubtotal: number
  /** Already filtered by visibility; render as-is. */
  feeLines: FeeLine[]
  tax: number
  registrantPays: number
  /** Deducted from payout. 0 when the registrant pays the processing fee. */
  processingDeduction: number
  yourPayout: number
}

/** The example panel is always illustrated against a $100.00 registration. */
export const PREVIEW_BASE_COST = 100

const toCents = (value: number): number => Number(value.toFixed(2))

export const calculateFeePreview = ({
  baseCost,
  leaguePercent,
  leagueFlat,
  leagueVisible,
  bearer,
  paymentVisible,
  taxRatePercent,
  providerFlat,
  providerPercent,
}: FeePreviewInput): FeePreviewResult => {
  // A $0 or negative cost is treated as free — a comp registration or a
  // 100% coupon stays free. Negative costs are technically unconfigurable but
  // we guard here for safety. The API enforces this; the preview matches it.
  if (baseCost <= 0) {
    return {
      invoiceSubtotal: 0,
      feeLines: [],
      tax: 0,
      registrantPays: 0,
      processingDeduction: 0,
      yourPayout: 0,
    }
  }

  // The league fee is additive: a percentage of the cost PLUS a flat amount.
  const leagueFee = toCents(
    baseCost * ((leaguePercent ?? 0) / 100) + (leagueFlat ?? 0)
  )

  // The league fee is league revenue and IS taxed. The processing fee is not.
  const taxableAmount = toCents(baseCost + leagueFee)
  const tax = toCents(taxableAmount * (taxRatePercent / 100))

  // What the league would net if it absorbed nothing — the amount the
  // processor actually moves.
  const netAmount = toCents(taxableAmount + tax)

  const registrantPaysProcessing = bearer === 'registrant'

  // The two bearers use deliberately different formulas.
  //
  // Absorbed: a straight deduction off the payout.
  //   118.09 * 2.85% + 1.00 = 4.37
  //
  // Registrant pays: grossed up so the league still nets the full amount. A
  // naive `netAmount * rate + flat` would leave the league short, because the
  // processor also takes its cut of the fee itself.
  //   (118.09 + 1.00) / (1 - 0.0285) - 118.09 = 4.49
  //
  // Guard: a provider rate >= 100% cannot be grossed up (divides by zero or
  // negative). Fall back to the non-grossed-up formula in this degenerate case.
  const denominator = 1 - providerPercent / 100
  const processingFee =
    registrantPaysProcessing && denominator > 0
      ? toCents((netAmount + providerFlat) / denominator - netAmount)
      : toCents(netAmount * (providerPercent / 100) + providerFlat)

  const registrantProcessingFee = registrantPaysProcessing ? processingFee : 0

  const feeLines: FeeLine[] = []
  if (leagueFee > 0 && leagueVisible) {
    feeLines.push({
      key: 'league_processing_fee',
      label: 'League Processing Fee',
      amount: leagueFee,
    })
  }
  // When the league absorbs the fee the registrant never paid it, so it is
  // never a registrant-facing line regardless of the visibility flag.
  if (registrantProcessingFee > 0 && paymentVisible) {
    feeLines.push({
      key: 'payment_processing_fee',
      label: 'Payment Processing Fee',
      amount: registrantProcessingFee,
    })
  }

  // Anything charged but not itemised folds into the subtotal.
  const itemised = feeLines.reduce((sum, line) => sum + line.amount, 0)
  const charged = leagueFee + registrantProcessingFee
  const invoiceSubtotal = toCents(baseCost + (charged - itemised))

  const registrantPays = toCents(invoiceSubtotal + itemised + tax)
  const processingDeduction = registrantPaysProcessing ? 0 : processingFee
  const yourPayout = registrantPaysProcessing
    ? netAmount
    : toCents(registrantPays - processingDeduction)

  return {
    invoiceSubtotal,
    feeLines,
    tax,
    registrantPays,
    processingDeduction,
    yourPayout,
  }
}
